import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { clientAPI, uiAPI, visitParentAPI, transportationParentAPI } from '../services/api';
import {
  Client,
  Transportation,
  UiRecord,
  Visit,
  VisitParentWithVisits,
  TransportationParentWithTransportations,
} from '../types';
import UiIncidentFields from './UiIncidentFields';
import {
  buildUiCreatePayload,
  emptyUiDraft,
  formatTransportationAssociationLabel,
  formatVisitAssociationLabel,
  RECENT_ASSOCIATION_LIMIT,
  sortByDescTime,
  UiDraft,
  uiDraftFromRecord,
  validateUiDraft,
} from '../utils/ui';

type AssociationKind = 'none' | 'visit' | 'transportation';

export interface StandaloneUiFormProps {
  open: boolean;
  onClose: () => void;
  /** When set, form edits this record instead of creating. */
  editingRecord?: UiRecord | null;
  onSaved?: () => void;
}

function flattenRecentVisits(
  occurrences: VisitParentWithVisits[],
  clientId: number
): { visit: Visit; label: string; sortTime: string }[] {
  const rows: { visit: Visit; label: string; sortTime: string }[] = [];
  for (const occ of occurrences) {
    for (const visit of occ.visits || []) {
      if (Number(visit.client_id) === clientId) {
        rows.push({
          visit,
          label: formatVisitAssociationLabel(visit),
          sortTime: visit.scheduled_start || visit.visit_date || visit.created_at || '',
        });
      }
    }
  }
  return sortByDescTime(rows, r => r.sortTime).slice(0, RECENT_ASSOCIATION_LIMIT);
}

function flattenRecentTransportations(
  occurrences: TransportationParentWithTransportations[],
  clientId: number
): { transportation: Transportation; label: string; sortTime: string }[] {
  const rows: { transportation: Transportation; label: string; sortTime: string }[] = [];
  for (const occ of occurrences) {
    for (const t of occ.transportations || []) {
      if (Number(t.client_id) === clientId) {
        rows.push({
          transportation: t,
          label: formatTransportationAssociationLabel(t),
          sortTime: t.scheduled_start || t.transportation_date || t.created_at || '',
        });
      }
    }
  }
  return sortByDescTime(rows, r => r.sortTime).slice(0, RECENT_ASSOCIATION_LIMIT);
}

const StandaloneUiForm: React.FC<StandaloneUiFormProps> = ({
  open,
  onClose,
  editingRecord = null,
  onSaved,
}) => {
  const { provider } = useAuth();
  const providerId = provider?.provider_id;
  const isEdit = !!editingRecord;

  const [clients, setClients] = useState<Client[]>([]);
  const [clientId, setClientId] = useState<number | ''>('');
  const [uiDraft, setUiDraft] = useState<UiDraft>(() => ({ ...emptyUiDraft(), has_ui: true }));
  const [associationKind, setAssociationKind] = useState<AssociationKind>('none');
  const [selectedVisitId, setSelectedVisitId] = useState<number | ''>('');
  const [selectedTransportationId, setSelectedTransportationId] = useState<number | ''>('');
  const [visitOptions, setVisitOptions] = useState<{ visit: Visit; label: string }[]>([]);
  const [transportationOptions, setTransportationOptions] = useState<
    { transportation: Transportation; label: string }[]
  >([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [loadingAssociations, setLoadingAssociations] = useState(false);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [showErrors, setShowErrors] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [initializedForId, setInitializedForId] = useState<number | null>(null);

  const resetForm = useCallback(() => {
    setClientId('');
    setUiDraft({ ...emptyUiDraft(), has_ui: true });
    setAssociationKind('none');
    setSelectedVisitId('');
    setSelectedTransportationId('');
    setVisitOptions([]);
    setTransportationOptions([]);
    setError(null);
    setShowErrors(false);
    setSuccess(null);
    setInitializedForId(null);
  }, []);

  useEffect(() => {
    if (!open) return;
    if (editingRecord) {
      if (initializedForId === editingRecord.ui_id) return;
      setUiDraft(uiDraftFromRecord(editingRecord));
      setClientId(Number(editingRecord.client_id));
      if (editingRecord.visit_id) {
        setAssociationKind('visit');
        setSelectedVisitId(Number(editingRecord.visit_id));
        setSelectedTransportationId('');
      } else if (editingRecord.transportation_id) {
        setAssociationKind('transportation');
        setSelectedTransportationId(Number(editingRecord.transportation_id));
        setSelectedVisitId('');
      } else {
        setAssociationKind('none');
        setSelectedVisitId('');
        setSelectedTransportationId('');
      }
      setError(null);
      setSuccess(null);
      setInitializedForId(editingRecord.ui_id);
      return;
    }
    resetForm();
  }, [open, editingRecord]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open || !providerId) return;
    let cancelled = false;
    (async () => {
      setLoadingClients(true);
      try {
        const data = await clientAPI.getClients(providerId);
        if (!cancelled) setClients(data);
      } catch {
        if (!cancelled) setError('Failed to load clients.');
      } finally {
        if (!cancelled) setLoadingClients(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, providerId]);

  useEffect(() => {
    if (!open || !providerId || clientId === '') {
      setVisitOptions([]);
      setTransportationOptions([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingAssociations(true);
      try {
        const cid = Number(clientId);
        const fetchLimit = RECENT_ASSOCIATION_LIMIT * 3;
        const [visitResp, transportResp] = await Promise.all([
          visitParentAPI.list(providerId, fetchLimit, 0, { clientId: cid }),
          transportationParentAPI.list(providerId, fetchLimit, 0, { clientId: cid }),
        ]);
        if (cancelled) return;
        const visitItems: VisitParentWithVisits[] = visitResp?.items ?? visitResp ?? [];
        const transportItems: TransportationParentWithTransportations[] =
          transportResp?.items ?? transportResp ?? [];
        let visits = flattenRecentVisits(visitItems, cid).map(r => ({
          visit: r.visit,
          label: r.label,
        }));
        let transports = flattenRecentTransportations(transportItems, cid).map(r => ({
          transportation: r.transportation,
          label: r.label,
        }));

        if (
          editingRecord?.visit_id &&
          Number(editingRecord.client_id) === cid &&
          !visits.some(v => Number(v.visit.visit_id) === Number(editingRecord.visit_id))
        ) {
          visits = [
            {
              visit: { visit_id: editingRecord.visit_id, client_id: cid } as Visit,
              label: `Linked visit #${editingRecord.visit_id}`,
            },
            ...visits,
          ];
        }
        if (
          editingRecord?.transportation_id &&
          Number(editingRecord.client_id) === cid &&
          !transports.some(
            t => Number(t.transportation.transportation_id) === Number(editingRecord.transportation_id)
          )
        ) {
          transports = [
            {
              transportation: {
                transportation_id: editingRecord.transportation_id,
                client_id: cid,
              } as Transportation,
              label: `Linked transportation #${editingRecord.transportation_id}`,
            },
            ...transports,
          ];
        }

        setVisitOptions(visits);
        setTransportationOptions(transports);
      } catch {
        if (!cancelled) setError('Failed to load recent visits or transportation records.');
      } finally {
        if (!cancelled) setLoadingAssociations(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, providerId, clientId, editingRecord]);

  const clientName = useMemo(() => {
    if (clientId === '') return '';
    const c = clients.find(x => Number(x.client_id) === Number(clientId));
    return c ? `${c.first_name} ${c.last_name}` : '';
  }, [clientId, clients]);

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    if (savingRef.current) return;
    if (!providerId) {
      setShowErrors(true);
      setError('Provider is required.');
      return;
    }
    if (clientId === '') {
      setShowErrors(true);
      setError('Please select a client.');
      return;
    }
    const draftErr = validateUiDraft(uiDraft);
    if (draftErr) {
      setShowErrors(true);
      setError(draftErr);
      return;
    }
    if (associationKind === 'visit' && selectedVisitId === '') {
      setShowErrors(true);
      setError('Please select a visit or choose no association.');
      return;
    }
    if (associationKind === 'transportation' && selectedTransportationId === '') {
      setShowErrors(true);
      setError('Please select a transportation record or choose no association.');
      return;
    }
    setShowErrors(false);

    savingRef.current = true;
    setSaving(true);
    setError(null);
    try {
      const payload = buildUiCreatePayload(uiDraft, {
        provider_id: providerId,
        client_id: Number(clientId),
        visit_id: associationKind === 'visit' ? Number(selectedVisitId) : null,
        transportation_id:
          associationKind === 'transportation' ? Number(selectedTransportationId) : null,
      });
      if (isEdit && editingRecord) {
        await uiAPI.update(editingRecord.ui_id, payload);
        setSuccess('Unusual incident updated successfully.');
      } else {
        await uiAPI.create(payload);
        setSuccess('Unusual incident reported successfully.');
      }
      onSaved?.();
      setTimeout(() => {
        handleClose();
      }, 1200);
    } catch (e: unknown) {
      const ex = e as { message?: string; response?: { data?: { detail?: string } } };
      setError(ex?.response?.data?.detail || ex?.message || 'Failed to save unusual incident.');
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {isEdit ? 'Edit Unusual Incident' : 'Report Unusual Incident'}
        <IconButton onClick={handleClose} size="small" aria-label="Close">
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {(loadingClients || loadingAssociations) && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={28} />
          </Box>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12}>
            <FormControl fullWidth required size="small" error={showErrors && clientId === ''}>
              <InputLabel>Client *</InputLabel>
              <Select
                value={clientId}
                label="Client *"
                onChange={(e: SelectChangeEvent<number | ''>) => {
                  setClientId(e.target.value as number | '');
                  setAssociationKind('none');
                  setSelectedVisitId('');
                  setSelectedTransportationId('');
                }}
              >
                <MenuItem value="">
                  <em>Select client</em>
                </MenuItem>
                {clients.map(c => (
                  <MenuItem key={String(c.client_id)} value={c.client_id as number}>
                    {c.first_name} {c.last_name}
                  </MenuItem>
                ))}
              </Select>
              {showErrors && clientId === '' && <FormHelperText>Client is required</FormHelperText>}
            </FormControl>
          </Grid>

          <UiIncidentFields
            variant="standalone"
            showErrors={showErrors}
            value={uiDraft}
            onChange={patch => setUiDraft(prev => ({ ...prev, ...patch }))}
          />

          <Grid item xs={12}>
            <FormControl fullWidth size="small">
              <InputLabel>Associate with (optional)</InputLabel>
              <Select
                value={associationKind}
                label="Associate with (optional)"
                onChange={(e: SelectChangeEvent<AssociationKind>) => {
                  setAssociationKind(e.target.value as AssociationKind);
                  setSelectedVisitId('');
                  setSelectedTransportationId('');
                }}
                disabled={clientId === ''}
              >
                <MenuItem value="none">None — standalone incident</MenuItem>
                <MenuItem value="visit">Visit</MenuItem>
                <MenuItem value="transportation">Transportation</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {associationKind === 'visit' && clientId !== '' && (
            <Grid item xs={12}>
              <FormControl fullWidth required size="small" error={showErrors && selectedVisitId === ''}>
                <InputLabel>Recent visit *</InputLabel>
                <Select
                  value={selectedVisitId}
                  label="Recent visit *"
                  onChange={(e: SelectChangeEvent<number | ''>) =>
                    setSelectedVisitId(e.target.value as number | '')
                  }
                >
                  <MenuItem value="">
                    <em>Select visit</em>
                  </MenuItem>
                  {visitOptions.map(opt => (
                    <MenuItem key={String(opt.visit.visit_id)} value={opt.visit.visit_id}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
                {showErrors && selectedVisitId === '' && <FormHelperText>Select a visit</FormHelperText>}
              </FormControl>
              {visitOptions.length === 0 && !loadingAssociations && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  No recent visits found for {clientName || 'this client'}.
                </Alert>
              )}
            </Grid>
          )}

          {associationKind === 'transportation' && clientId !== '' && (
            <Grid item xs={12}>
              <FormControl fullWidth required size="small" error={showErrors && selectedTransportationId === ''}>
                <InputLabel>Recent transportation *</InputLabel>
                <Select
                  value={selectedTransportationId}
                  label="Recent transportation *"
                  onChange={(e: SelectChangeEvent<number | ''>) =>
                    setSelectedTransportationId(e.target.value as number | '')
                  }
                >
                  <MenuItem value="">
                    <em>Select transportation</em>
                  </MenuItem>
                  {transportationOptions.map(opt => (
                    <MenuItem
                      key={String(opt.transportation.transportation_id)}
                      value={opt.transportation.transportation_id}
                    >
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
                {showErrors && selectedTransportationId === '' && (
                  <FormHelperText>Select a transportation record</FormHelperText>
                )}
              </FormControl>
              {transportationOptions.length === 0 && !loadingAssociations && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  No recent transportation records found for {clientName || 'this client'}.
                </Alert>
              )}
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving || !!success}
          sx={{ backgroundColor: '#2D4B73' }}
        >
          {saving ? (
            <CircularProgress size={22} color="inherit" />
          ) : isEdit ? (
            'Done'
          ) : (
            'Submit Report'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StandaloneUiForm;
